// Global Cart API
// Plain ESM module that wraps existing cart-functions utilities with a global-friendly API.
// External callers (Replo pages, marketing scripts, inline onclick handlers) use
// window.pvolve.cart.add() / .checkout() to add products to cart.

import {
	addItemsToCart,
	getCart,
	getDOMCart,
	getDOMCartSectionApiIds,
	hasMatchingBundleInCart,
	isMembershipCartItem,
	openCartDrawerWithOptimisticItem,
	renderResponseToCartDrawer,
	updateCartRequest,
	type AddToCartItem,
} from '@/scripts/core/cart-functions'
import type { Cart, CartErrorResponse } from '@/scripts/shopify'
import { publish, PUB_SUB_EVENTS } from '@/scripts/core/global'
import {
	syncPayLaterCartAttribute,
	syncInternalAttribFromCookie,
} from '@/scripts/core/cart-attribution'
import {
	resolveProductInput,
	fetchProductById,
	fetchProductByHandle,
	getFirstAvailableVariant,
	type ShopifyProductJSON,
	type ShopifyVariantJSON,
} from '@/scripts/core/product-resolver'
import {
	getCachedProduct,
	getCachedProductById,
	getCachedProductByVariantId,
	getTriggeredOfferRules,
	refreshCartSnapshot,
} from '@/scripts/core/product-cache'
import { buildOptimisticCartItems } from '@/scripts/cart/optimistic-cart'
import { CartDrawer } from '@/scripts/cart/cart-drawer'
import { forceReleaseAllLocks, isLocked } from '@/scripts/cart/optimistic-lock'
import type { LockToken } from '@/scripts/cart/optimistic-lock'
import { cartDebug, summarizeCartItems } from '@/scripts/cart/optimistic-debug'
import {
	getCartState,
	isCartStateInitialized,
} from '@/scripts/cart/cart-state'
import { showToast } from '@/scripts/core/toast'

export type CartApiOptions = {
	quantity?: number
	variantId?: number
	sellingPlanId?: number
	properties?: Record<string, string>
	openCart?: boolean
	goToCheckout?: boolean
	payLater?: string | null
	purchaseAsGift?: boolean
	/** When true, treat numeric input as a product ID (not variant ID) and resolve to its first available variant */
	isProductId?: boolean
}

export type CartApiResult = {
	success: boolean
	error?: string
}

let isProcessing = false
let lastProcessTime = 0
let isBackForwardRevalidationInFlight = false
let hasInitializedBackForwardCartRevalidation = false
const DEBOUNCE_MS = 500
const NUMERIC_ID_PATTERN = /^\d+$/

const SCULPT_9_PRODUCT_HANDLE = 'sculpt-9'
const SCULPT_9_PRODUCT_ID = 7989447622713
const SCULPT_9_SUBSCRIPTION_VARIANT_IDS = new Set([
	44866918252601,
	44866918285369,
	44866918318137,
])
const SCULPT_9_SELLING_PLAN_TO_VARIANT_ID = new Map([
	[6393757753, 44866918252601],
	[6393790521, 44866918285369],
	[6393823289, 44866918318137],
])
const SCULPT_9_VARIANT_TO_SELLING_PLAN_ID = new Map(
	Array.from(SCULPT_9_SELLING_PLAN_TO_VARIANT_ID.entries()).map(
		([sellingPlanId, variantId]) => [variantId, sellingPlanId]
	)
)
const SCULPT_9_SELLING_PLAN_TO_DURATION = new Map([
	[6393757753, '1'],
	[6393790521, '3'],
	[6393823289, '6'],
])
const SCULPT_9_SWAP_TOAST_ID = 'pvolve-sculpt-9-swap-toast'
const SCULPT_9_ONE_TIME_VARIANT_IDS = new Set([44866918219833])

function isBackForwardRestore(event: PageTransitionEvent): boolean {
	if (event.persisted) return true

	const navigationEntry = window.performance.getEntriesByType(
		'navigation'
	)[0] as PerformanceNavigationTiming | undefined

	return navigationEntry?.type === 'back_forward'
}

async function revalidateCartAfterHistoryRestore(): Promise<void> {
	if (isBackForwardRevalidationInFlight) return

	isBackForwardRevalidationInFlight = true
	cartDebug('cart-api:history-restore:revalidate:start')

	try {
		isProcessing = false
		forceReleaseAllLocks()
		refreshCartSnapshot()

		const cartEl = getDOMCart()
		if (cartEl instanceof CartDrawer) {
			cartEl.dataset.noOpen = 'true'
			await cartEl.forceRefreshFromServer()
		}

		cartDebug('cart-api:history-restore:revalidate:done', {
			hasCartDrawer: cartEl instanceof CartDrawer,
		})
	} catch (e) {
		cartDebug('cart-api:history-restore:revalidate:error', {
			error: e instanceof Error ? e.message : String(e),
		})
	} finally {
		isBackForwardRevalidationInFlight = false
	}
}

function initBackForwardCartRevalidation(): void {
	if (hasInitializedBackForwardCartRevalidation) return

	hasInitializedBackForwardCartRevalidation = true
	window.addEventListener('pageshow', (event) => {
		if (!isBackForwardRestore(event)) return
		void revalidateCartAfterHistoryRestore()
	})
}

function getProductForOptimistic(
	variantId: number
): ShopifyProductJSON | null {
	return getCachedProductByVariantId(variantId) ?? null
}

/** Synchronous cache-only lookup by product ID. Used by the pre-resolve optimistic
 *  path when the caller passed `isProductId: true` — lets us render a real row
 *  before the async `fetchProductById` variant-resolution call runs. */
function getProductForOptimisticByProductId(
	productId: number
): ShopifyProductJSON | null {
	return getCachedProductById(productId) ?? null
}

/** Synchronous duplicate-bundle check against client-side cart state.
 *  Mirrors hasMatchingBundleInCart (`cart-functions.ts:109-122`) but reads from
 *  cart-state instead of requiring an async /cart.js fetch. Used to short-circuit
 *  cartApiAdd before it fires the optimistic render — avoids the "forever loading"
 *  bug where a duplicate click injects a row, acquires the lock, and then bails in
 *  the duplicate-skip branch without releasing anything.
 *
 *  Returns null when cart-state isn't initialized yet (caller falls back to the
 *  async /cart.js check further down the pipeline). */
function hasMatchingBundleInCartStateSync(
	variantId: number,
	payLater: string | null
): boolean | null {
	if (!isCartStateInitialized()) return null
	const normalizedPayLater = (payLater ?? '').toLowerCase() || null
	return getCartState().items.some((item) => {
		if (item.variantId !== variantId) return false
		if (!item.isBundle) return false
		const itemPayLater =
			(item.properties?._pay_later ?? '').toLowerCase() || null
		return itemPayLater === normalizedPayLater
	})
}

function isExclusiveProduct(product: ShopifyProductJSON | null): boolean {
	if (!product) return false
	const title = (product.title ?? '').toLowerCase()
	const handle = (product.handle ?? '').toLowerCase()
	const productType = (product.type ?? '').toLowerCase()

	return (
		title.includes('bundle') ||
		handle.includes('bundle') ||
		productType === 'bundle' ||
		title.includes('membership') ||
		productType === 'streaming' ||
		productType.includes('membership')
	)
}

function isSculpt9SubscriptionVariantId(variantId: number): boolean {
	return SCULPT_9_SUBSCRIPTION_VARIANT_IDS.has(variantId)
}

function isSculpt9KnownOneTimeVariantId(variantId: number): boolean {
	return SCULPT_9_ONE_TIME_VARIANT_IDS.has(variantId)
}

function isSculpt9Product(
	product: ShopifyProductJSON | null | undefined
): product is ShopifyProductJSON {
	return product?.handle === SCULPT_9_PRODUCT_HANDLE
}

function getSculpt9ProductForOptimistic(
	input: number
): ShopifyProductJSON | null {
	return (
		getProductForOptimisticByProductId(input) ??
		(input === SCULPT_9_PRODUCT_ID
			? getCachedProduct(SCULPT_9_PRODUCT_HANDLE) ?? null
			: null)
	)
}

async function fetchSculpt9ProductByInput(
	input: number
): Promise<ShopifyProductJSON | null> {
	const product = await fetchProductById(input)
	if (product) return product

	if (input !== SCULPT_9_PRODUCT_ID) return null

	try {
		return await fetchProductByHandle(SCULPT_9_PRODUCT_HANDLE)
	} catch (e) {
		cartDebug('cart-api:add:sculpt-9-handle-fetch-failed', {
			error: e instanceof Error ? e.message : String(e),
		})
		return null
	}
}

function getSculpt9SellingPlanDuration(
	sellingPlanId: number,
	product?: ShopifyProductJSON | null
): string | undefined {
	const knownDuration = SCULPT_9_SELLING_PLAN_TO_DURATION.get(sellingPlanId)
	if (knownDuration) return knownDuration

	for (const group of product?.selling_plan_groups ?? []) {
		for (const plan of group.selling_plans ?? []) {
			if (plan.id !== sellingPlanId) continue
			return plan.name.match(/(1|3|6)\s*month/i)?.[1]
		}
	}

	return undefined
}

function getSculpt9VariantDuration(
	variant?: ShopifyVariantJSON | null
): string | undefined {
	if (!variant) return undefined
	const titleDuration = variant.title?.match(/(1|3|6)\s*months?/i)?.[1]
	if (titleDuration) return titleDuration
	return variant.sku?.match(/-(1|3|6)M$/i)?.[1]
}

function getSculpt9CartItemDuration(
	item: Cart['items'][number]
): string | undefined {
	const titleDuration = String(item.variant_title ?? '').match(
		/(1|3|6)\s*months?/i
	)?.[1]
	if (titleDuration) return titleDuration
	return String(item.sku ?? '').match(/-(1|3|6)M$/i)?.[1]
}

function isSculpt9SubscriptionVariant(
	variant?: ShopifyVariantJSON | null
): boolean {
	if (!variant) return false
	return (
		isSculpt9SubscriptionVariantId(variant.id) ||
		Boolean(getSculpt9VariantDuration(variant))
	)
}

function getSculpt9QuantityForDuration(duration: string): number {
	const quantity = Number(duration)
	return [1, 3, 6].includes(quantity) ? quantity : 1
}

function getSculpt9SellingPlanIdForDuration(
	product: ShopifyProductJSON,
	duration: string,
	variant?: ShopifyVariantJSON
): number | undefined {
	const variantPlanId =
		variant?.selling_plan_allocations?.at(-1)?.selling_plan?.id ??
		variant?.selling_plan_allocations?.at(-1)?.selling_plan_id
	if (variantPlanId) return variantPlanId

	for (const group of product.selling_plan_groups ?? []) {
		for (const plan of group.selling_plans ?? []) {
			if (plan.name.match(new RegExp(`\\b${duration}\\s*month`, 'i'))) {
				return plan.id
			}
		}
	}

	return undefined
}

function getSculpt9VariantForDuration(
	product: ShopifyProductJSON,
	duration: string
): ShopifyVariantJSON | undefined {
	const titlePattern = new RegExp(`^${duration}\\s*months?$`, 'i')
	const skuPattern = new RegExp(`-${duration}M$`, 'i')

	return product.variants.find((variant) => {
		return (
			titlePattern.test(variant.title ?? '') ||
			skuPattern.test(variant.sku ?? '')
		)
	})
}

function resolveSculpt9SubscriptionSelection(
	product: ShopifyProductJSON,
	sellingPlanId?: number,
	variantId?: number
):
	| { variantId: number; sellingPlanId: number; duration: string }
	| undefined {
	if (!isSculpt9Product(product)) return undefined

	const currentVariant = variantId
		? product.variants.find((variant) => variant.id === variantId)
		: undefined
	const duration =
		(sellingPlanId
			? getSculpt9SellingPlanDuration(sellingPlanId, product)
			: undefined) ?? getSculpt9VariantDuration(currentVariant)
	if (!duration) return undefined

	const variant = getSculpt9VariantForDuration(product, duration)
	if (!variant) return undefined
	const resolvedSellingPlanId =
		getSculpt9SellingPlanIdForDuration(product, duration, variant) ??
		sellingPlanId
	if (!resolvedSellingPlanId) return undefined

	return {
		variantId: variant.id,
		sellingPlanId: resolvedSellingPlanId,
		duration,
	}
}

function productHasVariant(product: ShopifyProductJSON, variantId: number): boolean {
	return product.variants.some((variant) => variant.id === variantId)
}

function productHasSellingPlan(
	product: ShopifyProductJSON,
	sellingPlanId: number
): boolean {
	return (product.selling_plan_groups ?? []).some((group) =>
		(group.selling_plans ?? []).some((plan) => plan.id === sellingPlanId)
	)
}

async function resolveCurrentSculpt9SubscriptionSelection(params: {
	input: number
	variantId: number
	sellingPlanId?: number
}): Promise<
	| { productId: number; variantId: number; sellingPlanId: number; quantity: number }
	| undefined
> {
	const { input, variantId, sellingPlanId } = params
	const cachedProduct =
		getCachedProductByVariantId(variantId) ??
		getCachedProductById(input) ??
		getCachedProduct(SCULPT_9_PRODUCT_HANDLE)
	let product = isSculpt9Product(cachedProduct) ? cachedProduct : null
	const isKnownProductionSculpt9Input =
		input === SCULPT_9_PRODUCT_ID || isSculpt9SubscriptionVariantId(input)

	if (!product) {
		try {
			product = await fetchProductByHandle(SCULPT_9_PRODUCT_HANDLE)
		} catch (e) {
			cartDebug('cart-api:add:sculpt-9-current-handle-fetch-failed', {
				error: e instanceof Error ? e.message : String(e),
			})
			return undefined
		}
	}

	if (!product) return undefined

	const isCurrentSculpt9Variant = productHasVariant(product, variantId)
	const isCurrentSculpt9SellingPlan =
		sellingPlanId != null ? productHasSellingPlan(product, sellingPlanId) : false
	const currentVariant = product.variants.find(
		(variant) => variant.id === variantId
	)
	const isCurrentSculpt9SubscriptionVariant =
		isCurrentSculpt9Variant && isSculpt9SubscriptionVariant(currentVariant)

	if (
		!isKnownProductionSculpt9Input &&
		!isCurrentSculpt9SubscriptionVariant &&
		!isCurrentSculpt9SellingPlan
	) {
		return undefined
	}

	const selection = resolveSculpt9SubscriptionSelection(
		product,
		sellingPlanId,
		variantId
	)
	if (!selection) return undefined

	return {
		productId: product.id,
		...selection,
		quantity: getSculpt9QuantityForDuration(selection.duration),
	}
}

/**
 * Detect whether an incoming numeric variant ID belongs to a Sculpt 9
 * one-time variant — on any store (production, QA, dev). Uses hardcoded
 * production IDs as the fast path; falls back to handle-based product
 * lookup for other stores.
 */
async function resolveSculpt9OneTimeCartAdd(
	variantId: number,
	normalizedInput: number
): Promise<
	| { isSculpt9OneTime: true; productId: number }
	| { isSculpt9OneTime: false }
> {
	// Fast path: hardcoded production IDs
	if (isSculpt9KnownOneTimeVariantId(variantId)) {
		return { isSculpt9OneTime: true, productId: SCULPT_9_PRODUCT_ID }
	}
	// Subscription variants are handled by resolveCurrentSculpt9SubscriptionSelection
	if (isSculpt9SubscriptionVariantId(variantId)) {
		return { isSculpt9OneTime: false }
	}

	// Dynamic check: find the Sculpt 9 product and verify the variant belongs to it
	let product =
		getCachedProductByVariantId(variantId) ??
		getCachedProductById(normalizedInput) ??
		getCachedProduct(SCULPT_9_PRODUCT_HANDLE)

	if (!product || !isSculpt9Product(product)) {
		try {
			product = await fetchProductByHandle(SCULPT_9_PRODUCT_HANDLE)
		} catch {
			return { isSculpt9OneTime: false }
		}
	}

	if (!product || !isSculpt9Product(product)) {
		return { isSculpt9OneTime: false }
	}

	const variant = product.variants.find((v) => v.id === variantId)
	if (!variant || isSculpt9SubscriptionVariant(variant)) {
		return { isSculpt9OneTime: false }
	}

	return { isSculpt9OneTime: true, productId: product.id }
}

function resolveSculpt9SubscriptionVariant(
	input: number | string,
	options: CartApiOptions
): number | string {
	if (typeof input !== 'number' || options.isProductId) return input
	const variantFromSellingPlan = options.sellingPlanId
		? SCULPT_9_SELLING_PLAN_TO_VARIANT_ID.get(options.sellingPlanId)
		: undefined
	if (!variantFromSellingPlan) return input

	if (
		input === SCULPT_9_PRODUCT_ID ||
		isSculpt9SubscriptionVariantId(input) ||
		options.variantId === SCULPT_9_PRODUCT_ID ||
		(options.variantId != null &&
			isSculpt9SubscriptionVariantId(options.variantId))
	) {
		return variantFromSellingPlan
	}

	return input
}

function getSculpt9SellingPlanId(variantId: number, options: CartApiOptions) {
	return (
		options.sellingPlanId ??
		SCULPT_9_VARIANT_TO_SELLING_PLAN_ID.get(variantId)
	)
}

function isSculpt9SubscriptionCartItem(
	item: Cart['items'][number],
	sculpt9ProductId = SCULPT_9_PRODUCT_ID
): boolean {
	if (!isSculpt9CartItem(item, sculpt9ProductId)) return false

	return (
		isSculpt9SubscriptionVariantId(item.id) ||
		Boolean(getSculpt9CartItemDuration(item)) ||
		Boolean(item.selling_plan_allocation)
	)
}

/** Detects Sculpt 9 one-time cart items on any store (production or QA/dev).
 *  Uses cart-item properties (handle, product_id, selling_plan_allocation)
 *  so it works without hardcoded variant IDs. */
function isSculpt9OneTimeCartItem(
	item: Cart['items'][number],
	sculpt9ProductId = SCULPT_9_PRODUCT_ID
): boolean {
	return (
		isSculpt9CartItem(item, sculpt9ProductId) &&
		!isSculpt9SubscriptionCartItem(item, sculpt9ProductId)
	)
}

function isSculpt9CartItem(
	item: Cart['items'][number],
	sculpt9ProductId = SCULPT_9_PRODUCT_ID
): boolean {
	const itemHandle = String(
		(item as Cart['items'][number] & { handle?: string; product_handle?: string })
			.handle ??
			(item as Cart['items'][number] & { handle?: string; product_handle?: string })
				.product_handle ??
			''
	)
	if (
		item.product_id !== SCULPT_9_PRODUCT_ID &&
		item.product_id !== sculpt9ProductId &&
		itemHandle !== SCULPT_9_PRODUCT_HANDLE
	) {
		return false
	}
	return true
}

function isSculpt9AttachedGwpCartItem(
	item: Cart['items'][number],
	parentIds: Set<number>,
	sculpt9OfferGiftVariantIds: Set<number>,
	sculpt9OfferHandles: Set<string>
): boolean {
	if (!item.properties?._gwp_item && !item.properties?._gwp_offer) {
		return false
	}

	const bundleParentId = Number(item.properties?._bundle_parent_id ?? 0)
	if (parentIds.has(bundleParentId)) return true

	if (sculpt9OfferGiftVariantIds.has(item.id)) return true

	const gwpOfferHandle = String(item.properties?._gwp_offer ?? '')
	if (gwpOfferHandle && sculpt9OfferHandles.has(gwpOfferHandle)) return true

	const triggerCopy = String(
		item.properties?._gwp_trigger_product_title ??
			item.properties?._bundle_parent_title ??
			''
	).toLowerCase()
	return /sculpt\s*9/i.test(triggerCopy)
}

function buildSculpt9CartCleanupUpdates(
	currentCart: Cart,
	incomingVariantId: number,
	incomingProductId: number,
	isSubscription = false
): {
	updates: { key: string; quantity: number }[]
	hadSculpt9InCart: boolean
	hadDifferentSculpt9InCart: boolean
} {
	const updatesByKey = new Map<string, number>()
	let hadSculpt9InCart = false
	let hadDifferentSculpt9InCart = false

	const parentIds = new Set<number>([
		SCULPT_9_PRODUCT_ID,
		incomingProductId,
		incomingVariantId,
	])
	const sculpt9OfferGiftVariantIds = new Set<number>()
	const sculpt9OfferHandles = new Set<string>()
	// Determine if the incoming variant is one-time. The caller passes
	// isSubscription when resolvedSellingPlanId is set, which is the
	// authoritative signal regardless of store (production, QA, dev).
	// Hardcoded production IDs are still used as the fast path when the
	// caller doesn't pass a definitive flag (backward compat).
	const isIncomingOneTime = isSubscription
		? false
		: SCULPT_9_ONE_TIME_VARIANT_IDS.has(incomingVariantId) ||
			!isSculpt9SubscriptionVariantId(incomingVariantId)

	for (const rule of getTriggeredOfferRules(incomingProductId, incomingVariantId)) {
		if (rule.giftProduct.variantId) {
			sculpt9OfferGiftVariantIds.add(rule.giftProduct.variantId)
		}
		if (rule.handle) {
			sculpt9OfferHandles.add(rule.handle)
		}
	}

	for (const item of currentCart.items) {
		if (isSculpt9CartItem(item, incomingProductId)) {
			hadSculpt9InCart = true
			parentIds.add(item.product_id)
			parentIds.add(item.id)

			// Preserve existing one-time items when adding another one-time.
			// Shopify's /cart/add.js will increment the quantity naturally.
			// Use dynamic detection so this works on QA/dev stores too.
			if (isIncomingOneTime && isSculpt9OneTimeCartItem(item, incomingProductId)) {
				continue
			}

			if (item.id !== incomingVariantId) {
				hadDifferentSculpt9InCart = true
			}
			updatesByKey.set(item.key, 0)
		}
	}

	if (updatesByKey.size > 0) {
		for (const item of currentCart.items) {
			if (
				isSculpt9AttachedGwpCartItem(
					item,
					parentIds,
					sculpt9OfferGiftVariantIds,
					sculpt9OfferHandles
				)
			) {
				updatesByKey.set(item.key, 0)
			}
		}
	} else {
		// No items removed — suppress the swap toast.
		hadSculpt9InCart = false
		hadDifferentSculpt9InCart = false
	}

	return {
		updates: Array.from(updatesByKey.entries()).map(([key, quantity]) => ({
			key,
			quantity,
		})),
		hadSculpt9InCart,
		hadDifferentSculpt9InCart,
	}
}

function showSculpt9SwapToast(): void {
	showToast('Sculpt 9 selection updated.', {
		id: SCULPT_9_SWAP_TOAST_ID,
		variant: 'success',
	})
}

function normalizeCartApiInput(input: number | string): number | string {
	if (typeof input === 'number') return input
	const trimmed = input.trim()
	if (!NUMERIC_ID_PATTERN.test(trimmed)) return input
	const parsed = Number(trimmed)
	return Number.isSafeInteger(parsed) ? parsed : input
}

export async function cartApiAdd(
	input: number | string,
	options: CartApiOptions = {}
): Promise<CartApiResult> {
	let normalizedInput = normalizeCartApiInput(input)
	normalizedInput = resolveSculpt9SubscriptionVariant(
		normalizedInput,
		options
	)
	cartDebug('cart-api:add:start', {
		input,
		normalizedInput,
		options,
	})
	const now = Date.now()
	if (isProcessing || now - lastProcessTime < DEBOUNCE_MS) {
		return { success: false, error: 'busy' }
	}

	isProcessing = true
	lastProcessTime = now

	try {
		const wantsOptimistic =
			!options.goToCheckout && options.openCart !== false
		let optimisticStarted = false
		let shouldResolveExclusiveConflicts = false
		const isTrialAdd =
			String(options.properties?._is_trial ?? '').toLowerCase() === 'true'
		let lockToken: LockToken | null = null
		let cartDrawer: CartDrawer | null = null

		// ── 0. Early sync duplicate-bundle short-circuit ────────────────
		// Before firing any optimistic render or network call, check if the
		// bundle is already in cart with the same pay-later state. If so:
		// - No optimistic row / no lock / no /cart/add.js / no duplication.
		// - For goToCheckout (PayPal/AfterPay express), still redirect.
		// - For regular adds, just open the drawer.
		// Falls through to the async check in Step 4 if cart-state isn't
		// initialized yet (first visits before /cart.js has hydrated it).
		if (
			typeof normalizedInput === 'number' &&
			!options.isProductId &&
			!options.variantId
		) {
			const syncDuplicate = hasMatchingBundleInCartStateSync(
				normalizedInput,
				options.payLater ?? null
			)
			if (syncDuplicate === true) {
				cartDebug('cart-api:add:sync-duplicate-skip', {
					variantId: normalizedInput,
					payLater: options.payLater ?? null,
					goToCheckout: options.goToCheckout ?? false,
				})
				if (options.goToCheckout) {
					window.location.href = '/checkout'
				} else if (options.openCart !== false) {
					const cartEl = getDOMCart()
					if (
						cartEl instanceof CartDrawer &&
						!cartEl.classList.contains('active')
					) {
						cartEl.open()
					}
				}
				isProcessing = false
				return { success: true }
			}
		}

		// ── 1. Pre-resolution optimistic attempt (numeric variant/product IDs) ──
		// Works for both variant-ID input and product-ID input (isProductId=true).
		// For product-ID input we look up the cached product directly and pick its
		// first available variant, so the drawer can render a real row *before* the
		// async fetchProductById variant-resolution call runs in Step 2.
		if (wantsOptimistic && typeof normalizedInput === 'number') {
			const cachedProduct = options.isProductId
				? getSculpt9ProductForOptimistic(normalizedInput)
				: getProductForOptimistic(normalizedInput)
			const sculpt9OptimisticSelection =
				options.isProductId &&
				isSculpt9Product(cachedProduct) &&
				(options.sellingPlanId || options.variantId)
					? resolveSculpt9SubscriptionSelection(
							cachedProduct,
							options.sellingPlanId,
							options.variantId
						)
					: undefined
			const renderVariantId = options.isProductId
				? (sculpt9OptimisticSelection?.variantId ??
					options.variantId ??
					(cachedProduct
						? getFirstAvailableVariant(cachedProduct)?.id
						: undefined) ??
					normalizedInput)
				: (options.variantId ?? normalizedInput)
			shouldResolveExclusiveConflicts =
				isTrialAdd || isExclusiveProduct(cachedProduct)
			try {
				const optimisticData = buildOptimisticCartItems({
					variantId: renderVariantId,
					product: cachedProduct ?? undefined,
					quantity: options.quantity ?? 1,
					payLater: options.payLater,
					purchaseAsGift: options.purchaseAsGift,
					isTrial:
						String(
							options.properties?._is_trial ?? ''
						).toLowerCase() === 'true',
				})
				const hasExclusiveAssociated =
					optimisticData.associated.some(
						(item) =>
							item.type === 'bundle_membership' ||
							item.type === 'alcm'
					)
				shouldResolveExclusiveConflicts =
					optimisticData.main.isExclusive ||
					hasExclusiveAssociated ||
					isTrialAdd
				cartDebug('cart-api:add:pre-resolve-optimistic', {
					mainVariantId: optimisticData.main.variantId,
					mainIsExclusive: optimisticData.main.isExclusive,
					mainIsSkeleton: optimisticData.main.isSkeleton ?? false,
					hasExclusiveAssociated,
					shouldResolveExclusiveConflicts,
					associated: optimisticData.associated.map((a) => ({
						type: a.type,
						variantId: a.variantId,
						title: a.title,
					})),
				})
				openCartDrawerWithOptimisticItem(
					optimisticData.main,
					optimisticData.associated
				)
				const cartEl = getDOMCart()
				if (cartEl instanceof CartDrawer) {
					cartDrawer = cartEl
					lockToken =
						cartDrawer.acquireOptimisticLock('cart-api-add')
					optimisticStarted = true
				}
			} catch (e) {
				cartDebug('cart-api:add:pre-resolve-optimistic-error', {
					error: e instanceof Error ? e.message : String(e),
				})
				console.warn(
					'[CartAPI] Optimistic pre-resolve path failed:',
					e
				)
			}
		}

		// ── 2. Resolve variant ID ───────────────────────────────────
		let variantId: number
		let resolvedSellingPlanId = options.sellingPlanId
		let resolvedQuantity = options.quantity ?? 1
		let isSculpt9CartAdd = false
		let sculpt9ProductId = SCULPT_9_PRODUCT_ID

		if (options.isProductId && typeof normalizedInput === 'number') {
			const product = await fetchSculpt9ProductByInput(normalizedInput)
			if (!product) {
				if (optimisticStarted) forceReleaseAllLocks()
				return {
					success: false,
					error: `Product ${normalizedInput} not found`,
				}
			}
			const variant = getFirstAvailableVariant(product)
			const sculpt9Selection =
				options.sellingPlanId || options.variantId
					? resolveSculpt9SubscriptionSelection(
							product,
							options.sellingPlanId,
							options.variantId
						)
					: undefined
			if (!variant && !sculpt9Selection) {
				if (optimisticStarted) forceReleaseAllLocks()
				return {
					success: false,
					error: `No available variant for product ${normalizedInput}`,
				}
			}
			variantId =
				sculpt9Selection?.variantId ??
				options.variantId ??
				(variant as NonNullable<typeof variant>).id
			resolvedSellingPlanId =
				sculpt9Selection?.sellingPlanId ?? resolvedSellingPlanId
			resolvedQuantity = sculpt9Selection
				? getSculpt9QuantityForDuration(sculpt9Selection.duration)
				: resolvedQuantity
			if (isSculpt9Product(product)) {
				isSculpt9CartAdd = true
				sculpt9ProductId = product.id
			}
		} else if (typeof normalizedInput === 'number') {
			// Pre-send sanity check: callers sometimes pass a product ID in the
			// variant slot (no `isProductId: true` flag). Shopify's /cart/add.js
			// rejects product IDs with a 422, which we then catch and retry as a
			// product ID — wasting ~1.5s and one extra /cart/add.js request per
			// click. If the collection seed has the product cached by product ID
			// but not by variant ID, auto-rewrite to the first available variant
			// before the network call. Respects `options.variantId` override.
			const cachedByVariant = getCachedProductByVariantId(normalizedInput)
			const cachedByProduct = getCachedProductById(normalizedInput)
			if (!cachedByVariant && cachedByProduct && !options.variantId) {
				const fallbackVariant = getFirstAvailableVariant(cachedByProduct)
				if (fallbackVariant) {
					cartDebug('cart-api:add:auto-rewrite-product-id-to-variant', {
						originalInput: normalizedInput,
						resolvedVariantId: fallbackVariant.id,
						productId: cachedByProduct.id,
						productHandle: cachedByProduct.handle,
					})
					variantId = fallbackVariant.id
					isSculpt9CartAdd = isSculpt9Product(cachedByProduct)
					if (isSculpt9CartAdd) sculpt9ProductId = cachedByProduct.id
				} else {
					variantId = normalizedInput
				}
			} else {
				variantId = options.variantId ?? normalizedInput
				if (isSculpt9Product(cachedByVariant)) {
					isSculpt9CartAdd = true
					sculpt9ProductId = cachedByVariant.id
				} else if (
					isSculpt9SubscriptionVariantId(variantId) ||
					isSculpt9KnownOneTimeVariantId(variantId)
				) {
					isSculpt9CartAdd = true
				}
			}
			if (!isSculpt9CartAdd) {
				const oneTimeResult = await resolveSculpt9OneTimeCartAdd(
					variantId,
					normalizedInput
				)
				if (oneTimeResult.isSculpt9OneTime) {
					isSculpt9CartAdd = true
					sculpt9ProductId = oneTimeResult.productId
				}
			}
			const sculpt9Selection =
				await resolveCurrentSculpt9SubscriptionSelection({
					input: normalizedInput,
					variantId,
					sellingPlanId: resolvedSellingPlanId,
				})
			if (sculpt9Selection) {
				variantId = sculpt9Selection.variantId
				resolvedSellingPlanId = sculpt9Selection.sellingPlanId
				resolvedQuantity = sculpt9Selection.quantity
				isSculpt9CartAdd = true
				sculpt9ProductId = sculpt9Selection.productId
			}
		} else {
			const resolved = await resolveProductInput(normalizedInput)
			variantId = options.variantId ?? resolved.variant.id
			if (isSculpt9Product(resolved.product)) {
				isSculpt9CartAdd = true
				sculpt9ProductId = resolved.product.id
			}
		}

		// ── 2b. Post-resolution optimistic attempt (handle/productId inputs) ──
		if (wantsOptimistic && !optimisticStarted) {
			const product = getProductForOptimistic(variantId)
			shouldResolveExclusiveConflicts =
				shouldResolveExclusiveConflicts ||
				isTrialAdd ||
				isExclusiveProduct(product)
			try {
				const optimisticData = buildOptimisticCartItems({
					variantId,
					product: product ?? undefined,
					quantity: options.quantity ?? 1,
					payLater: options.payLater,
					purchaseAsGift: options.purchaseAsGift,
					isTrial:
						String(
							options.properties?._is_trial ?? ''
						).toLowerCase() === 'true',
				})
				const hasExclusiveAssociated =
					optimisticData.associated.some(
						(item) =>
							item.type === 'bundle_membership' ||
							item.type === 'alcm'
					)
				shouldResolveExclusiveConflicts =
					optimisticData.main.isExclusive ||
					hasExclusiveAssociated ||
					isTrialAdd
				cartDebug('cart-api:add:post-resolve-optimistic', {
					variantId,
					mainIsExclusive: optimisticData.main.isExclusive,
					mainIsSkeleton: optimisticData.main.isSkeleton ?? false,
					hasExclusiveAssociated,
					shouldResolveExclusiveConflicts,
					associated: optimisticData.associated.map((a) => ({
						type: a.type,
						variantId: a.variantId,
						title: a.title,
					})),
				})
				openCartDrawerWithOptimisticItem(
					optimisticData.main,
					optimisticData.associated
				)
				const cartEl = getDOMCart()
				if (cartEl instanceof CartDrawer) {
					cartDrawer = cartEl
					lockToken =
						cartDrawer.acquireOptimisticLock('cart-api-add')
					optimisticStarted = true
				}
			} catch (e) {
				cartDebug('cart-api:add:post-resolve-optimistic-error', {
					error: e instanceof Error ? e.message : String(e),
				})
				console.warn(
					'[CartAPI] Optimistic post-resolve path failed:',
					e
				)
			}
		}

		// ── 3. Build cart item ──────────────────────────────────────
		const properties: Record<string, string> = {
			...(options.properties ?? {}),
		}
		if (options.purchaseAsGift) {
			properties['_purchase_as_gift'] = 'true'
		}
		if (options.payLater) {
			properties['_pay_later'] = options.payLater
		}

		const item: AddToCartItem = {
			id: variantId,
			quantity: resolvedQuantity,
			properties,
		}
		const sculpt9SellingPlanId = isSculpt9SubscriptionVariantId(variantId)
			? getSculpt9SellingPlanId(variantId, options)
			: undefined
		if (resolvedSellingPlanId || sculpt9SellingPlanId) {
			item.selling_plan = resolvedSellingPlanId ?? sculpt9SellingPlanId
		}

		// ── 4. Pre-add cleanup + cart add ────────────────────────────
		const sections = getDOMCartSectionApiIds()
		const needsCleanup = !!options.payLater
		let shouldShowSculpt9SwapToast = false
		let cart: Awaited<ReturnType<typeof addItemsToCart>>

		// Check if the same bundle is already in cart with matching payLater state
		const currentCart = await getCart()
		const payLaterToCompare = options.payLater || null
		const isSameBundleInCart =
			'status' in currentCart
				? false
				: hasMatchingBundleInCart(
						currentCart.items,
						variantId,
						payLaterToCompare
					)

		cartDebug('cart-api:duplicate-check', {
			variantId,
			payLaterToCompare,
			isSameBundleInCart,
		})

		if (!('status' in currentCart) && isSculpt9CartAdd) {
			const sculpt9Cleanup = buildSculpt9CartCleanupUpdates(
				currentCart,
				variantId,
				sculpt9ProductId,
				resolvedSellingPlanId != null
			)
			if (sculpt9Cleanup.updates.length > 0) {
				cartDebug('cart-api:add:sculpt-9-cleanup', {
					variantId,
					updates: sculpt9Cleanup.updates.length,
					hadDifferentSculpt9InCart:
						sculpt9Cleanup.hadDifferentSculpt9InCart,
				})
				const cleanupResult = await updateCartRequest(
					[],
					sculpt9Cleanup.updates
				)
				if ('status' in cleanupResult) {
					if (optimisticStarted) forceReleaseAllLocks()
					return {
						success: false,
						error:
							cleanupResult.description ||
							cleanupResult.message ||
							'Sculpt 9 cart cleanup failed',
					}
				}
				shouldShowSculpt9SwapToast = sculpt9Cleanup.hadSculpt9InCart
			}
		}

		// Run pay-later cleanup BEFORE the duplicate check so stale
		// memberships/GWPs are removed even when we skip the add.
		if (needsCleanup) {
			cartDebug('cart-api:add:pre-cleanup', {
				variantId,
				payLater: options.payLater ?? null,
				isSameBundleInCart,
			})
			await preAddCartCleanup(
				variantId,
				options.payLater ?? null,
				currentCart
			)
		}

		// Skip the add entirely when the exact bundle + pay-later state is
		// already in cart. Shopify's /cart/add.js would otherwise increment
		// the quantity (or create a duplicate line), producing the
		// "click pay-later twice → 2 bundles" bug. Cleanup above still ran,
		// so any stale memberships/GWPs are gone.
		if (isSameBundleInCart) {
			cartDebug('cart-api:add:same-bundle-skip', {
				variantId,
				optimisticStarted,
				needsCleanup,
				goToCheckout: options.goToCheckout ?? false,
			})

			// Reconcile drawer state. If the pre-resolve block injected an
			// optimistic row and set line prices to loading, OR if cleanup
			// just modified cart state via /cart/update.js, we need fresh
			// sections — otherwise the lock stays held, optimistic row
			// stays in the DOM, and/or post-cleanup state isn't rendered.
			// forceRefreshFromServer releases locks, fetches sections, and
			// atomically re-renders.
			if (optimisticStarted && cartDrawer) {
				void cartDrawer.forceRefreshFromServer()
			} else if (needsCleanup) {
				const cartEl = getDOMCart()
				if (cartEl instanceof CartDrawer) {
					void cartEl.forceRefreshFromServer()
				}
			}

			if (options.goToCheckout) {
				window.location.href = '/checkout'
			} else if (options.openCart !== false) {
				const cartEl = getDOMCart()
				if (
					cartEl instanceof CartDrawer &&
					!cartEl.classList.contains('active')
				) {
					cartEl.open()
				}
			}
			isProcessing = false
			return { success: true }
		}

		cart = await addItemsToCart(
			[item],
			sections,
			optimisticStarted
				? {
						conflictMode: shouldResolveExclusiveConflicts
							? 'exclusive_only'
							: 'none',
					}
				: undefined
		)

		// Shopify cart endpoints are session-locked. If a parallel /cart/update.js
		// request wins the lock (apps/pixels), /cart/add can return 409. Retry once
		// after a short delay to let the competing request finish.
		if ('status' in cart && cart.status === 409) {
			cartDebug('cart-api:add:retry-on-409', {
				variantId,
			})
			await new Promise((resolve) => setTimeout(resolve, 450))
			cart = await addItemsToCart(
				[item],
				sections,
				optimisticStarted
					? {
							conflictMode: shouldResolveExclusiveConflicts
								? 'exclusive_only'
								: 'none',
						}
					: undefined
			)
		}

		cartDebug('cart-api:add:addItemsToCart-response', {
			hasError: 'status' in cart,
			hasSections: 'sections' in cart && Boolean(cart.sections),
			itemCount: 'items' in cart ? cart.items.length : undefined,
			items: 'items' in cart ? summarizeCartItems(cart.items) : undefined,
		})

		// If failed and input was a raw number, retry as product ID lookup
		if (
			'status' in cart &&
			typeof normalizedInput === 'number' &&
			!options.variantId &&
			!options.isProductId
		) {
			console.warn(
				`[CartAPI] Variant ${normalizedInput} failed, retrying as product ID lookup`
			)
			if (optimisticStarted) {
				forceReleaseAllLocks()
				optimisticStarted = false
				lockToken = null
				cartDrawer = null
			}
			window.__skipGwpAdditions = false
			const retryProduct = await fetchProductById(normalizedInput)
			if (retryProduct) {
				const variant = getFirstAvailableVariant(retryProduct)
				if (variant) {
					item.id = variant.id
					variantId = variant.id
					cart = await addItemsToCart(
						[item],
						sections,
						optimisticStarted
							? {
									conflictMode:
										shouldResolveExclusiveConflicts
											? 'exclusive_only'
											: 'none',
								}
							: undefined
					)
				}
			}
		}

		// ── 7. Handle result ────────────────────────────────────────
		if ('status' in cart) {
			if (optimisticStarted) {
				forceReleaseAllLocks()
				if (cartDrawer) {
					void cartDrawer.forceRefreshFromServer()
				}
			}
			cartDebug('cart-api:add:error', {
				error: cart.description || cart.message || 'Unknown error',
				status: cart.status,
			})
			console.error('[CartAPI] Add to cart failed:', cart)
			return {
				success: false,
				error: cart.description || cart.message || 'Unknown error',
			}
		}

		// Fire-and-forget side effects AFTER the critical path
		refreshCartSnapshot()
		trackProductAdded(cart)

		publish(PUB_SUB_EVENTS.cartUpdate, {
			source: 'cart-api',
			productVariantId: variantId,
		})

		if (options.goToCheckout) {
			if (optimisticStarted) forceReleaseAllLocks()
			window.location.href = '/checkout'
		} else if (options.openCart !== false) {
			if (
				optimisticStarted &&
				cartDrawer &&
				lockToken &&
				'sections' in cart &&
				cart.sections
			) {
				cartDebug('cart-api:add:handle-server-response', {
					variantId,
					lockToken,
					conflictsResolved: cart._conflictsResolved,
				})
				// When exclusive conflicts are resolved, the cart state on server
				// changes but the initial /cart/add.js response sections are stale
				// (rendered before server-side update-instructions ran). Strip the
				// sections so handleServerResponse fetches fresh ones instead of
				// rendering stale HTML that still shows evicted items.
				if (cart._conflictsResolved) {
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					delete (cart as any).sections
				}
				// Lock-aware path: handleServerResponse manages the update cycle
				cartDrawer.handleServerResponse(cart, lockToken)
			} else {
				cartDebug('cart-api:add:render-response-directly', {
					variantId,
				})
				if (optimisticStarted) forceReleaseAllLocks()
				await renderResponseToCartDrawer(cart)
			}
		}

		// Attribution sync AFTER the update cycle — these hit Shopify's
		// session-locked cart endpoint and must not compete with the
		// background update cycle (add GWP, remove membership, etc.)
		const runAttribution = () => {
			void Promise.all([
				syncPayLaterCartAttribute(options.payLater ?? null),
				syncInternalAttribFromCookie(),
			]).catch((e) =>
				console.error('[CartAPI] Attribution sync error:', e)
			)
		}
		if (!isLocked()) {
			runAttribution()
		} else {
			const poll = setInterval(() => {
				if (!isLocked()) {
					clearInterval(poll)
					runAttribution()
				}
			}, 200)
		}

		if (shouldShowSculpt9SwapToast) {
			showSculpt9SwapToast()
		}

		return { success: true }
	} catch (e) {
		cartDebug('cart-api:add:exception', {
			error: e instanceof Error ? e.message : String(e),
		})
		forceReleaseAllLocks()
		const message = e instanceof Error ? e.message : 'Unknown error'
		console.error('[CartAPI] Error:', e)
		return { success: false, error: message }
	} finally {
		isProcessing = false
	}
}

export async function cartApiCheckout(
	input: number | string,
	options: CartApiOptions & { payLater: string }
): Promise<CartApiResult> {
	const shouldGoToCheckout =
		options.payLater === 'afterpay' || options.payLater === 'paypal'

	return cartApiAdd(input, {
		...options,
		goToCheckout: shouldGoToCheckout || options.goToCheckout,
		openCart: shouldGoToCheckout ? false : options.openCart,
	})
}

/**
 * Before adding to cart, clean up conflicting line items:
 * 1. When using pay-later (truemed/afterpay/paypal), remove memberships (not subscription-compatible)
 * 2. When using truemed, remove GWP items (not eligible)
 * Note: We no longer remove the same bundle variant - this is handled by the caller
 */
async function preAddCartCleanup(
	variantId: number,
	payLater: string | null,
	existingCart?: Cart | CartErrorResponse
): Promise<void> {
	const currentCart = existingCart ?? (await getCart())
	if ('status' in currentCart) return

	const updates: Record<string, number> = {}

	// Pay-later methods don't support subscriptions — remove memberships
	if (payLater) {
		for (const item of currentCart.items) {
			if (isMembershipCartItem(item)) {
				updates[item.key] = 0
			}
		}
	}

	// TrueMed is not eligible for GWP
	if (payLater === 'truemed') {
		for (const item of currentCart.items) {
			if (item.properties?._gwp_item) {
				updates[item.key] = 0
			}
		}
	}

	if (Object.keys(updates).length === 0) return

	cartDebug('cart-api:add:pre-cleanup-updates', {
		variantId,
		payLater,
		updates,
	})

	await fetch(window.routes.cart_update_url, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ updates }),
	})
		.then((r) =>
			r
				.clone()
				.json()
				.catch(() => null)
				.then((body) => ({ ok: r.ok, status: r.status, body }))
		)
		.then((result) => {
			cartDebug('cart-api:add:pre-cleanup-response', result)
		})
}

function trackProductAdded(cart: { items: { [key: string]: unknown }[] }) {
	try {
		if (!window.SegmentUser) return

		for (const item of cart.items) {
			void window.SegmentUser.sendSegmentEvent(null, {
				eventName: 'Product Added',
				sent_from: 'Pvolve Web Script',
				brand: item.vendor as string,
				category: item.product_type as string,
				url: window.location.origin + (item.url as string),
				product_id: item.product_id as number,
				sku: item.sku as string,
				name: item.product_title as string,
				price: ((item.final_price as number) ?? 0) / 100,
				quantity: item.quantity as number,
				variant: (item.variant_title as string) || '',
				shopify_product_id: item.product_id as number,
				shopify_variant_id: item.id as number,
				currency: 'USD',
				image_url: item.image as string,
			})
		}
	} catch (e) {
		console.error('[CartAPI] Error tracking Product Added:', e)
	}
}

export function initCartApi() {
	initBackForwardCartRevalidation()

	const add = (input: number | string, options?: CartApiOptions) =>
		cartApiAdd(input, options)

	const addWithMembership = (
		input: number | string,
		options?: CartApiOptions
	) => cartApiAdd(input, options)

	const checkout = (
		input: number | string,
		options: CartApiOptions & { payLater: string }
	) => cartApiCheckout(input, options)

	// New API namespace
	window.pvolve = {
		...(window.pvolve ?? {}),
		cart: { add, addWithMembership, checkout },
		toast: { show: showToast },
	}

	// Legacy compat shims
	window.dispatchCartAddEvent = (
		productId: string | number,
		options?: CartApiOptions
	) => {
		return cartApiAdd(normalizeCartApiInput(productId), {
			...options,
			isProductId: true,
		})
	}

	window.addToCartWithMembership = (
		variantId: string | number,
		qty?: number,
		openCart?: boolean,
		payLater?: string,
		goToCheckout?: boolean
	) =>
		cartApiAdd(normalizeCartApiInput(variantId), {
			quantity: qty,
			openCart,
			payLater: payLater || null,
			goToCheckout,
		})

	// Alpine.store('utils').cart shim for legacy Skyline callers
	shimAlpineStore()
}

function shimAlpineStore() {
	const cartShim = {
		checkoutWithDeferredPayment: (
			variantId: number | string,
			_product: unknown,
			quantity?: number,
			provider?: string,
			purchaseAsGift?: boolean
		) => {
			const id = normalizeCartApiInput(variantId)
			return cartApiCheckout(id, {
				quantity: quantity ?? 1,
				payLater: provider || '',
				purchaseAsGift: purchaseAsGift ?? false,
			})
		},
		addProductByID: (
			variant: { id: number } | number | string,
			quantity?: number,
			sellingPlanId?: number,
			_productType?: string,
			_product?: unknown,
			openCart?: boolean,
			purchaseAsGift?: boolean
		) => {
			const id =
				typeof variant === 'object' && variant !== null
					? variant.id
					: normalizeCartApiInput(variant)
			return cartApiAdd(id, {
				quantity: quantity ?? 1,
				sellingPlanId,
				openCart: openCart ?? true,
				purchaseAsGift: purchaseAsGift ?? false,
			})
		},
	}

	const storeMap: Record<string, unknown> = {
		utils: { cart: cartShim },
	}

	if (!window.Alpine?.store) {
		window.Alpine = {
			store: (name: string) => storeMap[name],
		}
	}
}
